import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await emailjs.send(
        'service_wct7hk7',
        'template_dbs1zib',
        {
          from_name: formData.name,
          from_email: formData.email,
          company: formData.company,
          subject: formData.subject,
          message: formData.message,
          email: formData.email,
        },
        'C5wz6GWLLjz7z6HFf'
      );

      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', subject: '', message: '' });
      setTimeout(() => setIsSubmitted(false), 5000);

    } catch (error) {
      console.error('EmailJS error:', error);
      alert('Failed to send. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />
      
      {/* Hero Section with Animated Gradient */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-[#1A3A5C] to-[#0D2137] animate-gradient"></div>
        
        {/* Moving Color Overlay */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-gold/10 animate-gradient"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gold/5 to-transparent animate-gradient" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 75%, rgba(201, 169, 98, 0.2) 0%, transparent 50%)`,
          }}></div>
        </div>
        
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
          <div className={`flex items-center gap-2 text-sm mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link to="/" className="text-gray-400 hover:text-gold transition-colors cursor-pointer">{t('contactPage.breadcrumb.home')}</Link>
            <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line text-gray-500`}></i>
            <span className="text-gold">{t('contactPage.breadcrumb.contact')}</span>
          </div>
          
          <div className="max-w-3xl">
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-12 h-px bg-gold"></div>
              <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('contactPage.hero.label')}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-7xl text-white leading-tight mb-6">
              {t('contactPage.hero.title1')} <span className="text-gold">{t('contactPage.hero.title2')}</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed">
              {t('contactPage.hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-start">            {/* Contact Form */}
<div className={`bg-white rounded-3xl p-6 md:p-10 shadow-xl border border-gray-100 h-fit ${isRTL ? 'lg:order-2' : ''}`}>              <h2 className="font-serif text-2xl md:text-3xl text-navy mb-8">{t('contactPage.form.title')}</h2>
              
              {isSubmitted && (
                <div className={`mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-green-700 text-sm md:text-base">{t('contactPage.form.success')}</span>
                </div>
              )}

              <form 
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit} 
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('contactPage.form.name')} *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-300 text-sm md:text-base"
                      placeholder={t('contactPage.form.namePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('contactPage.form.email')} *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-300 text-sm md:text-base"
                      placeholder={t('contactPage.form.emailPlaceholder')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('contactPage.form.company')}</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-300 text-sm md:text-base"
                      placeholder={t('contactPage.form.companyPlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('contactPage.form.subject')} *</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-300 cursor-pointer text-sm md:text-base"
                    >
                      <option value="">{t('contactPage.form.subjectPlaceholder')}</option>
                      <option value="investment">{t('contactPage.form.subjectInvestment')}</option>
                      <option value="partnership">{t('contactPage.form.subjectPartnership')}</option>
                      <option value="startup">{t('contactPage.form.subjectStartup')}</option>
                      <option value="general">{t('contactPage.form.subjectGeneral')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contactPage.form.message')} *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={13}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-300 resize-none text-sm md:text-base"
                    placeholder={t('contactPage.form.messagePlaceholder')}
                  ></textarea>
                  <div className={`text-sm text-gray-400 mt-1 ${isRTL ? 'text-left' : 'text-right'}`}>
                    {formData.message.length}/500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 bg-gradient-to-r from-gold to-gold-dark text-navy font-semibold rounded-xl hover:shadow-lg hover:shadow-gold/20 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-3 disabled:opacity-70 text-sm md:text-base ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xl"></i>
                      {t('contactPage.form.sending')}
                    </>
                  ) : (
                    <>
                      {t('contactPage.form.submit')}
                      <i className={`ri-send-plane-line text-xl ${isRTL ? 'rotate-180' : ''}`}></i>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className={`space-y-8 ${isRTL ? 'lg:order-1' : ''}`}>
              <div>
                <h2 className="font-serif text-2xl md:text-3xl text-navy mb-6">{t('contactPage.info.title')}</h2>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                  {t('contactPage.info.description')}
                </p>
              </div>

              {/* WhatsApp */}
              <a href="https://wa.me/97154888408" target="_blank" rel="noopener noreferrer" className={`group flex items-start gap-5 p-5 md:p-6 bg-gray-50 rounded-2xl hover:bg-gold/5 transition-all duration-300 cursor-pointer ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <div className="w-12 h-12 md:w-14 md:h-14 animated-gradient-box rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-whatsapp-line text-xl md:text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <div className="text-xs md:text-sm text-gray-500 mb-1">WhatsApp</div>
                  <div className="text-lg md:text-xl font-semibold text-navy" dir="ltr">+971 54 888 4088</div>
                </div>
                {/* WhatsApp QR Code */}
              <div className="flex flex-col items-center flex-shrink-0">
                <img
                  src="/whatsapp-qr-black.png"
                  alt="WhatsApp QR"
                  className="w-24 h-24 rounded-lg"
                />
              </div>
              </a>

              {/* Landline */}
              <a href="tel:+97148886288" className={`group flex items-start gap-5 p-5 md:p-6 bg-gray-50 rounded-2xl hover:bg-gold/5 transition-all duration-300 cursor-pointer ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <div className="w-12 h-12 md:w-14 md:h-14 animated-gradient-box rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-phone-line text-xl md:text-2xl text-white"></i>
                </div>
                <div>
                  <div className="text-xs md:text-sm text-gray-500 mb-1">Landline</div>
                  <div className="text-lg md:text-xl font-semibold text-navy" dir="ltr">+971 4 888 6288</div>
                </div>
              </a>

              {/* Email */}
              <a href="mailto:info@alebel.com" className={`group flex items-start gap-5 p-5 md:p-6 bg-gray-50 rounded-2xl hover:bg-gold/5 transition-all duration-300 cursor-pointer ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <div className="w-12 h-12 md:w-14 md:h-14 animated-gradient-box rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-mail-line text-xl md:text-2xl text-white"></i>
                </div>
                <div>
                  <div className="text-xs md:text-sm text-gray-500 mb-1">{t('contactPage.info.email')}</div>
                  <div className="text-lg md:text-xl font-semibold text-navy">info@alebel.com</div>
                </div>
              </a>

              {/* Website */}
              <a href="https://www.alebel.com" target="_blank" rel="noopener noreferrer" className={`group flex items-start gap-5 p-5 md:p-6 bg-gray-50 rounded-2xl hover:bg-gold/5 transition-all duration-300 cursor-pointer ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <div className="w-12 h-12 md:w-14 md:h-14 animated-gradient-box rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-global-line text-xl md:text-2xl text-white"></i>
                </div>
                <div>
                  <div className="text-xs md:text-sm text-gray-500 mb-1">{t('contactPage.info.website')}</div>
                  <div className="text-lg md:text-xl font-semibold text-navy">www.alebel.com</div>
                </div>
              </a>

              {/* Location */}
              <div className={`flex items-start gap-5 p-5 md:p-6 bg-gray-50 rounded-2xl ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <div className="w-12 h-12 md:w-14 md:h-14 animated-gradient-box rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-xl md:text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <div className="text-xs md:text-sm text-gray-500 mb-1">{t('contactPage.info.location')}</div>
                  <div className="text-lg md:text-xl font-semibold text-navy">{t('contactPage.info.locationValue')}</div>
                  <div className="text-gray-600 text-xs md:text-sm mt-1">{t('contactPage.info.locationDesc')}</div>
                </div>
                {/* QR Code */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <img
                    src="/location-qr-black.png"
                    alt="Location QR Code"
                    className="w-24 h-24 rounded-lg"
                  />
                </div>
              </div>

            </div>
          </div>

          {/* Full Width Map */}
          <div className="mt-16 md:mt-24">
            <div className="rounded-3xl overflow-hidden shadow-2xl h-[400px] md:h-[500px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3608.016312871703!2d55.323430876155484!3d25.270036677664166!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5d0044880699%3A0x140396cb2920741c!2sAl%20Ebel%20Company!5e0!3m2!1sen!2sae!4v1777721773841!5m2!1sen!2sae"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="ALEBEL Investment Location">
              </iframe>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
